import clsx from 'clsx';
import { AlertTriangle, CheckCircle2, Info, X } from 'lucide-react';
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

type ToastKind = 'success' | 'error' | 'info';

type Toast = { id: number; kind: ToastKind; text: string };

type ToastApi = {
  push: (kind: ToastKind, text: string) => void;
  success: (text: string) => void;
  error: (text: string) => void;
  info: (text: string) => void;
};

const ToastContext = createContext<ToastApi | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const nextId = useRef(1);

  const remove = useCallback((id: number) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const push = useCallback(
    (kind: ToastKind, text: string) => {
      const id = nextId.current++;
      setToasts((current) => [...current.slice(-3), { id, kind, text }]);
      window.setTimeout(() => remove(id), kind === 'error' ? 8000 : 4000);
    },
    [remove],
  );

  const api = useMemo<ToastApi>(
    () => ({
      push,
      success: (text) => push('success', text),
      error: (text) => push('error', text),
      info: (text) => push('info', text),
    }),
    [push],
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div className="pointer-events-none fixed inset-x-2 bottom-2 z-[100] flex flex-col items-center gap-2 sm:inset-x-auto sm:bottom-4 sm:right-4 sm:items-end">
        {toasts.map((toast) => (
          <div
            key={toast.id}
            className={clsx(
              'pointer-events-auto flex w-full max-w-md items-start gap-2 rounded-lg border px-3 py-2 text-sm shadow-lg shadow-black/40',
              toast.kind === 'success' && 'border-emerald-900 bg-emerald-950/90 text-emerald-100',
              toast.kind === 'error' && 'border-red-900 bg-red-950/90 text-red-100',
              toast.kind === 'info' && 'border-zinc-700 bg-zinc-900/95 text-zinc-100',
            )}
          >
            <span className="mt-0.5 shrink-0">
              {toast.kind === 'success' ? (
                <CheckCircle2 className="size-4 text-emerald-400" />
              ) : toast.kind === 'error' ? (
                <AlertTriangle className="size-4 text-red-400" />
              ) : (
                <Info className="size-4 text-sky-400" />
              )}
            </span>
            <span className="min-w-0 flex-1 break-words">{toast.text}</span>
            <button
              type="button"
              aria-label="Скрыть"
              onClick={() => remove(toast.id)}
              className="shrink-0 rounded p-0.5 text-zinc-400 hover:text-zinc-100"
            >
              <X className="size-3.5" />
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi {
  const context = useContext(ToastContext);
  if (!context) throw new Error('useToast must be used inside ToastProvider');
  return context;
}
